/**
 * Gate hook that every `useGhostwriterFor*` hook calls to ensure
 * the ghostwriter lifecycle is initialized and the user is enrolled
 * in any ghostwriter proctor bucket.
 *
 * Returns five flags:
 *   - `isEnabled` → user is in *any* ghostwriter bucket
 *     (async_auto_apply_submit, sync_prefill_submit, deprecated,
 *     hybrid_automation, or sync_auto_apply). Loading semantics:
 *       - `null` while any proctor check is still resolving
 *       - `true` if any bucket is `true`
 *       - `false` only when all buckets are explicitly `false`
 *   - `isSyncPrefill` → user is in the sync_prefill_submit (synchronous flow)
 *     bucket specifically. Use to branch UI/copy on flow variant.
 *   - `isDeprecated` → user is in the deprecated (skip-eligibility) bucket
 *     specifically. Internal-only signal used by `useGhostwriterFor*`
 *     hooks to bypass the per-job eligibility backend call.
 *   - `isHybridAutomation` → user is in the hybrid_automation bucket specifically
 *     (sync_prefill_submit's synchronous Prefill flow plus the CAPTCHA / 2FA
 *     intervention screen). See RN-3367.
 *   - `isSyncAutoApply` → user is in the sync_auto_apply bucket specifically
 *     (layered-read: allowlist + real-time eligibility read on ViewJob).
 *
 * Note: this hook intentionally does *not* expose `isAsyncAutoApply`; the
 * gate is the union of buckets, and the variant flags are `isSyncPrefill`,
 * `isDeprecated`, `isHybridAutomation`, and `isSyncAutoApply`. Internal code
 * that needs the async_auto_apply_submit-bucket
 * selector specifically can call `useIsGhostwriterAsyncAutoApply` from
 * `GhostwriterContext`.
 *
 * The `UserDidLogout` broadcast subscription (Layer 1) is managed
 * by `ghostwriterLifecycle.setupLogoutBroadcast`, called once during
 * the first `ensureGhostwriterInitialized`.
 */

import {
  useIsGhostwriterAsyncAutoApply,
  useIsGhostwriterSyncPrefill,
  useIsGhostwriterDeprecated,
  useIsGhostwriterHybridAutomation,
  useIsGhostwriterSyncAutoApply,
} from "./GhostwriterContext";
import { ensureGhostwriterInitialized, waitForAccountId } from "./ghostwriterLifecycle";
import { useTareData } from "../../native-modules/tare/TareDataContext";

import { useEffect } from "react";
import { Platform } from "react-native";

export interface GhostwriterGateResult {
  isEnabled: boolean | null;
  isSyncPrefill: boolean | null;
  isDeprecated: boolean | null;
  isHybridAutomation: boolean | null;
  isSyncAutoApply: boolean | null;
}

function deriveIsEnabled(
  isAsyncAutoApply: boolean | null,
  isSyncPrefill: boolean | null,
  isDeprecated: boolean | null,
  isHybridAutomation: boolean | null,
  isSyncAutoApply: boolean | null,
): boolean | null {
  if (
    isAsyncAutoApply === true ||
    isSyncPrefill === true ||
    isDeprecated === true ||
    isHybridAutomation === true ||
    isSyncAutoApply === true
  )
    return true;
  if (
    isAsyncAutoApply === null ||
    isSyncPrefill === null ||
    isDeprecated === null ||
    isHybridAutomation === null ||
    isSyncAutoApply === null
  )
    return null;
  return false;
}

export function useGhostwriterGate(): GhostwriterGateResult {
  const tareData = useTareData();
  const accountId = tareData.accountStatus?.accountId ?? null;
  const isLoggedIn = tareData.accountStatus?.isLoggedIn;
  const isAsyncAutoApply = useIsGhostwriterAsyncAutoApply();
  const isSyncPrefill = useIsGhostwriterSyncPrefill();
  const isDeprecated = useIsGhostwriterDeprecated();
  const isHybridAutomation = useIsGhostwriterHybridAutomation();
  const isSyncAutoApply = useIsGhostwriterSyncAutoApply();

  useEffect(() => {
    if (accountId) {
      void ensureGhostwriterInitialized(accountId);
      return;
    }

    // On Android, accountStatus.accountId may not be populated at
    // mount time even when the user is logged in. Wait for the
    // native DataUpdated broadcast, then re-read account status.
    if (Platform.OS === "android" && isLoggedIn !== false) {
      let cancelled = false;
      void waitForAccountId().then((resolvedId) => {
        if (!cancelled) {
          void ensureGhostwriterInitialized(resolvedId);
        }
      });
      return () => {
        cancelled = true;
      };
    }

    void ensureGhostwriterInitialized(null);
  }, [accountId, isLoggedIn]);

  return {
    isEnabled: deriveIsEnabled(
      isAsyncAutoApply,
      isSyncPrefill,
      isDeprecated,
      isHybridAutomation,
      isSyncAutoApply,
    ),
    isSyncPrefill,
    isDeprecated,
    isHybridAutomation,
    isSyncAutoApply,
  };
}
